/**
 * draftStore — local, account-free draft metadata.
 *
 * The builder is usable with no login: a "draft" is a site that lives
 * entirely in the browser (its Yjs doc in OPFS-sqlite keyed by id, its
 * git history in an OPFS repo keyed by id, its images in OPFS under
 * `sites/<id>/assets`). This module owns only the small METADATA index
 * — name, slug, template, timestamps — in localStorage, plus a pointer
 * to the draft the editor should open by default.
 *
 * A draft only becomes a cloud site when the user signs in and publishes
 * online; until then nothing here ever touches the network. Local ids are
 * prefixed `local-` so the rest of the app can tell a local draft from a
 * server-minted site id at a glance (see isLocalId).
 */

const DRAFTS_KEY = "wb:drafts";
const CURRENT_KEY = "wb:current-draft";
const LOCAL_PREFIX = "local-";

export interface LocalDraft {
  readonly id: string;
  readonly name: string;
  /** vanity slug used for the would-be public host; defaults from name. */
  readonly slug: string;
  readonly templateId: string;
  readonly createdAt: number;
  readonly updatedAt: number;
  /**
   * Set once the draft has been published online: the cloud site it pushes
   * to. The draft's local Yjs doc stays the working copy; publishing online
   * just serializes + pushes it to this cloud repo (re-publish reuses these,
   * skipping a second createSite).
   */
  readonly cloudSiteId?: string;
  readonly cloudSlug?: string;
}

export function isLocalId(id: string): boolean {
  return id.startsWith(LOCAL_PREFIX);
}

function safeParse(raw: string | null): LocalDraft[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? (v as LocalDraft[]) : [];
  } catch {
    return [];
  }
}

function readAll(): LocalDraft[] {
  if (typeof localStorage === "undefined") return [];
  return safeParse(localStorage.getItem(DRAFTS_KEY));
}

function writeAll(drafts: ReadonlyArray<LocalDraft>): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
}

/** kebab-case a free-text name into a URL-safe slug, never empty. */
export function slugify(name: string): string {
  const s = name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "") // strip combining accents
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  return s || "untitled";
}

/** Newest-first list of all local drafts. */
export function listDrafts(): LocalDraft[] {
  return readAll().sort((a, b) => b.updatedAt - a.updatedAt);
}

export function getDraft(id: string): LocalDraft | null {
  return readAll().find((d) => d.id === id) ?? null;
}

function now(): number {
  return Date.now();
}

export function createDraft(input: {
  name: string;
  templateId: string;
  slug?: string;
}): LocalDraft {
  const ts = now();
  const draft: LocalDraft = {
    id: `${LOCAL_PREFIX}${crypto.randomUUID()}`,
    name: input.name.trim() || "Untitled site",
    slug: input.slug?.trim() || slugify(input.name),
    templateId: input.templateId,
    createdAt: ts,
    updatedAt: ts,
  };
  writeAll([draft, ...readAll()]);
  setCurrentDraftId(draft.id);
  return draft;
}

export function updateDraft(
  id: string,
  patch: Partial<Omit<LocalDraft, "id" | "createdAt">>,
): LocalDraft | null {
  const all = readAll();
  const idx = all.findIndex((d) => d.id === id);
  if (idx < 0) return null;
  const next: LocalDraft = { ...all[idx]!, ...patch, updatedAt: now() };
  all[idx] = next;
  writeAll(all);
  return next;
}

/** Bump updatedAt without other changes (e.g. on edit activity). */
export function touchDraft(id: string): void {
  updateDraft(id, {});
}

/**
 * Remove a draft's metadata AND best-effort purge its local storage:
 * the OPFS git repo (`/<id>`), the asset dir (`sites/<id>`), and the
 * sqlite doc file (`builder-site-<id>.sqlite3`). All purges are
 * best-effort — failures (unsupported browser, already gone) are
 * swallowed; the metadata removal is what makes the draft "deleted".
 */
export async function deleteDraft(id: string): Promise<void> {
  writeAll(readAll().filter((d) => d.id !== id));
  if (getCurrentDraftId() === id) clearCurrentDraftId();

  // The sqlite doc lives in the OPFS SAH POOL's opaque store, not at the OPFS
  // root — removing a root-level `builder-site-<id>.sqlite3` is a no-op (that
  // was the leak that filled the pool). Free the real slot via the pool's
  // unlink. Lazily import so this module stays worker/sqlite-free until a delete
  // actually happens. Best-effort — a busy pool is covered by on-demand growth.
  void import("@fredericrous/yjs-sqlite-provider")
    .then(({ SahPoolBackend }) =>
      SahPoolBackend.unlinkDoc(`builder-site-${id}.sqlite3`),
    )
    .catch(() => {});

  const root = await navigator.storage?.getDirectory?.().catch(() => null);
  if (!root || typeof root.removeEntry !== "function") return;
  const rm = (dir: FileSystemDirectoryHandle, name: string) =>
    Promise.resolve(dir.removeEntry(name, { recursive: true })).catch(() => {});
  await Promise.all([
    // git repo (`/<id>`) and assets (`sites/<id>`) ARE regular OPFS entries.
    rm(root, id),
    (async () => {
      const sites = await root.getDirectoryHandle("sites").catch(() => null);
      if (sites && typeof sites.removeEntry === "function") await rm(sites, id);
    })(),
  ]);
}

export function getCurrentDraftId(): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem(CURRENT_KEY);
}

export function setCurrentDraftId(id: string): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(CURRENT_KEY, id);
}

export function clearCurrentDraftId(): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(CURRENT_KEY);
}

/**
 * The draft the editor should open at `/`: the current pointer if it still
 * resolves, else the most-recently-updated draft. Returns null when there
 * are no drafts yet (caller creates a first one).
 */
export function resolveActiveDraft(): LocalDraft | null {
  const current = getCurrentDraftId();
  if (current) {
    const d = getDraft(current);
    if (d) return d;
  }
  return listDrafts()[0] ?? null;
}
